import { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { LinearStage } from '../../utils/commercialDashboard';

export const NAUTILUS = {
  deepOcean: '#0f2134',
  sand: '#ccba9b',
  slate: '#5d6d7e',
  buoy: '#e67e22',
} as const;

export const STAGE_RAMP: Record<LinearStage | 'won', string> = {
  new: '#1b3a5a',
  qualified: '#22496f',
  opportunity: '#2b5b85',
  bidding: '#3d6f97',
  negotiation: '#7b93a4',
  won: NAUTILUS.sand,
};

const channel = (value: number) => {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

const luminance = (hex: string) => {
  const raw = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4].map((offset) => parseInt(raw.slice(offset, offset + 2), 16));
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

const contrast = (a: string, b: string) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

export const inkOn = (fill: string) =>
  contrast(fill, '#ffffff') >= contrast(fill, NAUTILUS.deepOcean) ? '#ffffff' : NAUTILUS.deepOcean;

export const stageFill = (status: string) => STAGE_RAMP[status as LinearStage | 'won'] ?? NAUTILUS.slate;

export type ConsolePalette = {
  isDark: boolean;
  border: string;
  rule: string;
  surface: string;
  header: string;
  inset: string;
  hover: string;
  ink: string;
  inkSoft: string;
  inkFaint: string;
  figure: string;
  rise: string;
  fall: string;
  wonInk: string;
  starting: string;
  startingInk: string;
};

export const useConsolePalette = (): ConsolePalette => {
  const theme = useTheme();
  return useMemo(() => {
    const isDark = theme.palette.mode === 'dark';
    return {
      isDark,
      border: isDark ? '#2c3e50' : '#e0e0e0',
      rule: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(15, 33, 52, 0.07)',
      surface: isDark ? NAUTILUS.deepOcean : '#ffffff',
      header: isDark ? 'rgba(255, 255, 255, 0.025)' : 'rgba(15, 33, 52, 0.025)',
      inset: isDark ? 'rgba(0, 0, 0, 0.2)' : 'rgba(15, 33, 52, 0.04)',
      hover: isDark ? 'rgba(204, 186, 155, 0.06)' : 'rgba(15, 33, 52, 0.04)',
      ink: theme.palette.text.primary,
      inkSoft: isDark ? '#b0bec5' : '#546e7a',
      inkFaint: isDark ? '#7f8c99' : '#8a96a3',
      figure: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
      rise: isDark ? '#7cc4a1' : '#2e7d5b',
      fall: NAUTILUS.buoy,
      wonInk: isDark ? NAUTILUS.sand : '#8a7553',
      starting: isDark ? 'rgba(61, 111, 151, 0.35)' : 'rgba(61, 111, 151, 0.14)',
      startingInk: isDark ? '#a9c9e3' : '#22496f',
    };
  }, [theme]);
};
